import { faChevronLeft } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Img, StarRating } from '../../flayr-components';
import ArrayMap from '../../lib/ArrayMap/ArrayMap';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';
import ScrollSpy from '../../lib/ScrollSpy/ScrollSpy';

interface IProfilePageNavbarProps {
  profileImg?: string;
  featured?: boolean;
  reviewScore?: number;
  reviewCount?: number;
  name?: string;
  links?: string[];
  cta?: (e: any) => React.ReactNode;
  activeSection?: string;
  message?: React.ReactNode;
  hideCancellationPolicy?: boolean;
  onBack?: (e: any) => void;
  onLearnMore?: (e?: any) => void;
  onLinkClick?: (e: any) => void;
  cancellationPolicyLabel?: string;
}

const ProfilePageNavbar: React.FC<React.PropsWithChildren<React.PropsWithChildren<IProfilePageNavbarProps>>> = ({
  profileImg,
  name,
  links = [],
  cta,
  onBack,
  onLearnMore,
  featured,
  reviewScore,
  reviewCount,
  message,
  hideCancellationPolicy,
  cancellationPolicyLabel = 'COVID cancellation policy',
}) => {
  const { inView, ref } = useInView();

  const CovidCancellationSection = (
    <>
      {!message && (
        <p hidden={hideCancellationPolicy}>
          {cancellationPolicyLabel}
          <span
            className="block text-xs text-center underline cursor-pointer"
            onClick={onLearnMore}
          >
            Learn more
          </span>
        </p>
      )}
      {!!message && (
        <div className="text-base">
          {message}
          <p className="text-sm" hidden={hideCancellationPolicy}>
            <span>COVID cancellation Policy.</span>
            <span
              className="ml-2 text-xs text-center underline cursor-pointer"
              onClick={onLearnMore}
            >
              Learn more
            </span>
          </p>
        </div>
      )}
    </>
  );

  return (
    <>
      <div className="flex items-center flex-none w-full px-4 text-white bg-black h-14">
        <button
          className="w-8 h-8 text-2xl focus:outline-none"
          onClick={onBack}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <section className="flex items-center mx-auto">
          <div className="mr-4 w-9 h-9">
            <Img src={profileImg} size="square" round />
          </div>
          <div>
            <div className="flex items-center">
              <span className="text-lg leading-none">{name}</span>
              {featured && (
                <span className="inline-block px-2 ml-2 text-[10px] uppercase border rounded font-light border-opacity-50">
                  recommended
                </span>
              )}
            </div>
            <div className="text-[10px]" hidden={!reviewScore}>
              <StarRating rating={1} starsCount={1} fillColor="white" />{' '}
              {reviewScore}{' '}
              <span className="font-light">({reviewCount} reviews)</span>
            </div>
          </div>

          <div className="flex mx-12 space-x-4">
            <ArrayMap
              items={links}
              render={(link, idx) => (
                <a
                  className={classNames(
                    'capitalize cursor-pointer hover:opacity-75',
                    { underline: idx === 0 }
                  )}
                  href={`#${link}`}
                >
                  {link}
                </a>
              )}
            />
          </div>
        </section>
        <div className="pr-12 text-sm leading-tight text-center">
          {CovidCancellationSection}
        </div>

        <div>{cta?.({ floating: false })}</div>
      </div>
      <div ref={ref} className="transform -translate-y-1" />
      {!inView && !!name && (
        <div className="fixed top-0 left-0 z-20 flex w-full p-2">
          <div className="flex items-center flex-none w-full px-4 mx-auto bg-gray-200 bg-opacity-75 rounded-lg lg:w-11/12 xl:w-10/12 backdrop-blur-5 h-14 backdrop-blur-10">
            <section className="flex items-center mx-auto">
              <div className="w-8 h-8 mr-4">
                <Img src={profileImg} size="square" round />
              </div>
              <div>
                <div className="flex items-center">
                  <span className="leading-none ">{name}</span>
                  {featured && (
                    <span className="inline-block px-2 ml-2 text-[10px] uppercase border rounded font-light border-gray-600">
                      recommended
                    </span>
                  )}
                </div>
                <div className="text-[10px]" hidden={!reviewScore}>
                  <StarRating rating={1} starsCount={1} fillColor="black" />{' '}
                  {reviewScore}{' '}
                  <span className="font-light">({reviewCount} reviews)</span>
                </div>
              </div>

              <div className="flex mx-12 space-x-4">
                <ScrollSpy
                  render={(activeItem) => {
                    return (
                      <ArrayMap
                        items={links}
                        render={(link) => (
                          <a
                            className={classNames(
                              'capitalize cursor-pointer hover:opacity-75',
                              {
                                underline:
                                  activeItem?.toLowerCase() ===
                                  link?.toLowerCase(),
                              }
                            )}
                            href={`#${link}`}
                          >
                            {link}
                          </a>
                        )}
                      />
                    );
                  }}
                />
              </div>
            </section>
            <div className="pr-8 text-sm leading-tight text-center">
              {CovidCancellationSection}
            </div>
            <div>{cta?.({ floating: true })}</div>
          </div>
        </div>
      )}
    </>
  );
};
export default ProfilePageNavbar;
